import React, { useState, useEffect, useMemo, MouseEvent } from "react";
import { injectIntl, IntlShape } from "react-intl";
import ReactModal from "react-modal";

import Button from "../../../components/Tailwind/Button";
import IntlMessages from "../../../utils/messages";
import { ActionTypes } from "../../../utils/types";
import { GroundGraphqlContextStore } from "../../../lib/ground-aws-graphql-core";
import Table from "../../../components/Table";
import { EnumPermissionEntity } from "../../../lib/ground-aws-graphql-core/api/graphql/types";
import Tag from "../../../components/Tag";
import {
  Checkpoint,
  CheckpointType,
} from "../../../lib/ground-aws-graphql-core/models/Api/Inspection";
import { getTranslation } from "../../../utils/translation";
import { displayDayDDMMYYYY_HHMM, DEFAULT_LIMIT } from "../../../utils/config";
import Badge from "../../Tailwind/Badge";

const Modal: React.FC<ReactModal.Props> = props => <ReactModal {...props} />;

interface Props {
  intl: IntlShape;
  checkpointType: CheckpointType;
  isOpen: boolean;
  onRequestClose: (e?: MouseEvent<HTMLButtonElement>) => void;
  onAddCheckpoint: (type: CheckpointType, checkpoint: Checkpoint) => void;
  onRemoveCheckpoint: (type: CheckpointType, checkpoint: Checkpoint) => void;
  items: {
    checkpointType: CheckpointType;
    checkpoint: Checkpoint;
    action: ActionTypes;
  }[];
}

const ModalAddCheckpoint: React.FC<Props> = ({
  checkpointType,
  isOpen,
  items,
  onRequestClose,
  onAddCheckpoint,
  onRemoveCheckpoint,
}) => {
  const [loading, setLoading] = useState(false);

  const elements = useMemo(
    () =>
      items?.filter(
        i =>
          i.action !== ActionTypes.TO_DELETE &&
          i.checkpointType.id === checkpointType.id
      ),
    [items, checkpointType.id]
  );

  const listCheckpoints = GroundGraphqlContextStore.useStoreActions(
    actions => actions.inspection.listCheckpoints
  );

  const checkpoints = GroundGraphqlContextStore.useStoreState(
    state => state.inspection.checkpoints.items
  );

  const total = GroundGraphqlContextStore.useStoreState(
    state => state.inspection.checkpoints.total
  );

  const centerTimezone = GroundGraphqlContextStore.useStoreState(
    state => state.center.centerTimezone
  );

  const fetchCheckpoints = (
    pageIndex = 0,
    showLoader = true,
    limit = DEFAULT_LIMIT
  ) => {
    if (showLoader) setLoading(true);
    listCheckpoints({
      checkpointTypeId: checkpointType.id.toString(),
      offset: pageIndex * limit,
      limit,
    }).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isOpen) fetchCheckpoints();
  }, [isOpen]);

  const handleClick = (checkpoint: Checkpoint) => {
    const exists = items.some(
      i =>
        i.checkpoint.id === checkpoint.id &&
        i.action !== ActionTypes.TO_DELETE &&
        i.checkpointType.id === checkpointType.id
    );

    if (!exists) onAddCheckpoint(checkpointType, checkpoint);
  };

  const tableHead = [
    "page.inspection.checkpoint.label",
    "page.inspection.checkpoint.created_at",
    "page.inspection.checkpoint.updated_at",
    "general.status",
  ];

  const tableBody = useMemo(
    () =>
      checkpoints?.map((checkpoint: Checkpoint) => ({
        id: checkpoint.id.toString(),
        rowElements: [
          {
            element: getTranslation(checkpoint.title),
            onCellClick: () => handleClick(checkpoint),
          },
          {
            element: displayDayDDMMYYYY_HHMM(
              checkpoint.created_at,
              centerTimezone
            ),
            onCellClick: () => handleClick(checkpoint),
          },
          {
            element: displayDayDDMMYYYY_HHMM(
              checkpoint.updated_at,
              centerTimezone
            ),
            onCellClick: () => handleClick(checkpoint),
          },
          {
            element: checkpoint.enabled ? (
              <Badge label="general.actif" bgClassName="bg-ground-green-100" />
            ) : (
              <Badge label="general.inactif" bgClassName="bg-red-500" />
            ),
            onCellClick: () => handleClick(checkpoint),
          },
        ],
      })) || [],
    [checkpoints, centerTimezone, handleClick]
  );

  return (
    <Modal
      ariaHideApp={false}
      style={{ overlay: { backgroundColor: "rgba(0, 0, 0, 0.5)" } }}
      isOpen={isOpen}
      shouldCloseOnOverlayClick
      onRequestClose={onRequestClose}
    >
      {loading && <div className="loading" />}

      {!loading && (
        <div className="py-6 px-8">
          <h3 className="text-16px text-ground-black-100 mb-4">
            <IntlMessages id="page.list.inspection.modal.checkpoints.title" />
          </h3>

          <Table
            head={tableHead}
            body={tableBody}
            noDataText="page.list.inspection.checkpoints.empty"
            permissionEntity={EnumPermissionEntity.CHECKPOINT}
            onChange={({ pageIndex }) => fetchCheckpoints(pageIndex, false)}
            paginationLimit={DEFAULT_LIMIT}
            paginationTotal={total}
            className="my-5"
          />

          <div className="flex py-4">
            <span>
              <IntlMessages
                id="page.inspection.round.form.checkpoints.selection.total"
                values={{ total: elements?.length }}
              />
            </span>
          </div>

          {elements?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {elements.map((item, index) => (
                <Tag
                  key={`${item.checkpoint.id}_${index}`}
                  id={`${item.checkpoint.id}`}
                  title={getTranslation(item.checkpoint.title)}
                  deleteCondition
                  handleDelete={() =>
                    onRemoveCheckpoint(checkpointType, item.checkpoint)
                  }
                />
              ))}
            </div>
          )}

          <div className="flex py-6 justify-center">
            <Button
              id="btn-close-checkpoint-modal"
              name="btn-close-checkpoint-modal"
              type="button"
              item={null}
              onClick={onRequestClose}
            >
              <span className="text-center">
                <IntlMessages id="general.validate.selection" />
              </span>
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default injectIntl(ModalAddCheckpoint);
